/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type MouseEvent, type ReactElement, cloneElement, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import StandardLoginForm from '@/components/login/standard-login-form';
import type { action as loginAction } from '@/routes/_empty.login';

interface NmSignInPanelProps {
    /**
     * The header's sign-in control. Rendered as-is with an `onClick` merged in — this
     * component does NOT wrap it in a `SheetTrigger`; see the note on the controlled Sheet
     * below.
     */
    trigger: ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>;
}

/**
 * NM's GUEST sign-in affordance: a right-side account panel, not a navigation.
 *
 * WHY THIS EXISTS. Live NM's header sign-in is a `<button>` with NO href —
 * `<button id="your-neimans-cta" aria-label="Sign In and Register - Your Neiman's Account
 * Panel">` in every guest capture on disk (`.reference/live-shots/owner-2026-08-22/
 * designers-nm-1280.html` @1280 and `.reference/homepage.mobile.html` @mobile, same node).
 * It opens a panel; it never leaves the page. Ours was an `<a href="/login">` at EVERY
 * width on BOTH brands, which is the defect the owner reported as "the mobile sign in
 * click behaviour showing for desktop".
 *
 * BG IS NOT THIS AND MUST NOT BECOME THIS. Live BG's guest control really is a link —
 * `<a class="account-header-menu-item__signin-register-link desktop-only" href="/e/login">`
 * (`.reference/bg-homepage.desktop.html:77799` and `bg-plp-womens-dresses.desktop.html:81800`,
 * two independent dumps). The brands genuinely diverge here, which is why the choice is a
 * `Record<Brand, …>` in user-actions.tsx rather than a prop on this component.
 *
 * THE CONTAINER IS MEASURED; THE CONTENTS ARE NOT. Live's panel is mounted lazily by a
 * separate `panelApp` bundle and appears in NO capture on disk (0 occurrences of
 * `panel-wrapper` / `account-access-wrapper` in either NM dump, against a `<div>` control of
 * 704/754). What IS on disk is that bundle's SHIPPED STYLESHEET
 * (`designers-nm-1280_files/panelApp.4a54d8ac0241624dedac.bundle.css`), which declares the box
 * outright and is the source of every geometry value below:
 *
 *     .panel                  { width:375px; height:100%; display:flex; flex-direction:column }
 *     @media (max-width:767px) .panel { width:90%; max-width:375px }
 *     .panel__overlay         { position:fixed; inset:0; z-index:1200 }
 *     .panel__overlay.…after-open { background-color:rgba(0,0,0,.5); transition .5s }
 *     .panel_right-direction  { transform:translateX(100%) }   → translateX(0) on open
 *     .panel__header-close    { float:right; width:40px; height:40px }
 *
 * so: right-side drawer, 375px, full height, 50% black scrim, slides in from the right, close
 * control top-right. `SheetContent side="right"` already ships all of that except the width.
 *
 * The panel's INTERNAL layout is `unmeasured` and is deliberately not invented here — it
 * renders our existing `StandardLoginForm`, the same component the login route, the login
 * modal and the cart checkout gate all mount. The stylesheet does establish that live's panel
 * holds a real sign-in FORM rather than a menu of links (`.account-access-wrapper` carries
 * `#email-input-field`, `#password-input-field`, `#kmsi-checkbox`, `.hyperlink-forgot-pw` and a
 * `.register-content`), which is why a form is the right thing to put in it — but not what it
 * looks like. Pixel parity for the panel body needs a live capture of the panel OPEN.
 *
 * FETCHER, NOT ROUTE NAVIGATION. Copied wholesale from `login/login-modal.tsx`, which solved
 * the same problem for the checkout gate: the login action returns data only on intermediate
 * outcomes (OTP prompt, error) and returns a redirect Response on success, so a submit cycle
 * that ends with no `fetcher.data` IS the success signal. Route-level `<Form>` would navigate
 * to /login to show an error, which would defeat the point of a panel.
 */
export function NmSignInPanel({ trigger }: NmSignInPanelProps): ReactElement {
    const { t } = useTranslation('header');
    const [open, setOpen] = useState(false);
    const fetcher = useFetcher<typeof loginAction>();
    const wasSubmittingRef = useRef(false);
    const [error, setError] = useState<string | undefined>();

    // Reset transient state each time the panel opens, so a previous failed attempt's
    // message does not greet the next opener.
    useEffect(() => {
        if (open) setError(undefined);
    }, [open]);

    useEffect(() => {
        const data = fetcher.data;
        if (!data) return;
        if (data.error) setError(data.error);
    }, [fetcher.data]);

    // Close on success. State went non-idle then back to idle with no `fetcher.data` means
    // the action redirected — see the block comment above.
    useEffect(() => {
        if (fetcher.state !== 'idle') {
            wasSubmittingRef.current = true;
            return;
        }
        if (!wasSubmittingRef.current) return;
        wasSubmittingRef.current = false;
        if (fetcher.data) return;
        setOpen(false);
    }, [fetcher.state, fetcher.data]);

    /**
     * CONTROLLED, WITH NO `SheetTrigger` — and the reason is worth keeping, because the
     * obvious version is the one that silently does nothing.
     *
     * `SheetTrigger asChild` composes its open handler with the child's through Radix's
     * `composeEventHandlers(..., { checkForDefaultPrevented: true })`. So a child that calls
     * `preventDefault()` to stop the link navigating ALSO stops the panel opening: measured,
     * both brands, five widths — the click was swallowed, `navigated: false`, and no panel
     * or scrim ever mounted. A trigger that suppresses navigation and a trigger that opens a
     * Radix surface are mutually exclusive by construction.
     *
     * WHY KEEP THE LINK AT ALL, when live NM's node is a bare `<button>` with no href. The
     * control is server-rendered and the panel needs JS. As an `<a href="/login">` it
     * degrades correctly: before hydration, on a slow connection, or with JS off, the click
     * reaches the login PAGE — and middle-click / open-in-new-tab keep working, which they
     * cannot on live. This is a deliberate divergence from live's element TYPE in exchange
     * for a behaviour no user can lose. Everything a shopper can perceive — the click opens
     * a right-side panel, the page does not move — matches live.
     */
    const triggerWithHandler = cloneElement(trigger, {
        onClick: (event: MouseEvent<HTMLElement>) => {
            // Let the browser handle the modified-click affordances rather than the panel.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            setOpen(true);
        },
    });

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            {triggerWithHandler}
            {/* `w-[90%] max-w-[375px]` reproduces live's two arms in one declaration: below
                768 live is 90%/max-375, at and above it is a flat 375. Those agree at every
                width at or above 417px and are identical below it, so no breakpoint is
                needed to express the pair. */}
            <SheetContent
                side="right"
                className="w-[90%] max-w-[375px] sm:max-w-[375px] gap-0 overflow-y-auto"
                data-testid="nm-sign-in-panel">
                <SheetHeader className="pb-2">
                    <SheetTitle className="type-label-large">{t('signIn')}</SheetTitle>
                </SheetHeader>
                <div className="px-4 pb-8">
                    <StandardLoginForm error={error} isPasswordlessEnabled={false} Form={fetcher.Form} />
                </div>
            </SheetContent>
        </Sheet>
    );
}

export default NmSignInPanel;
